import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '@/store/auth';
import { toastApp } from '@/components/toast-message/sonner';
import { getErrorMessage } from '@/utils/get-error';
import {
    fetchHold353Claims,
    sendHold353Claims,
    type Hold353Response,
} from './hold353';
import { LuckyDrawBannerPopup } from './lucky-draw-banner-popup';
import { LuckyDrawPopup } from './lucky-draw-popup';
import { useTradeMax } from './useTradeMax';

/**
 * Oct 2026 campaign: every opened trade = 1 Lucky Draw.
 * - mount / after closing a trade: banner suggesting to open a trade
 * - after opening a trade: 6-box lucky draw
 */
export function DailyReward() {
    const [isBannerOpen, setIsBannerOpen] = useState(false);
    const [isDrawOpen, setIsDrawOpen] = useState(false);
    const address = useAuth((state) => state.address);
    const claimsRef = useRef<Promise<Hold353Response> | null>(null);
    const goTradeMax = useTradeMax();

    useEffect(() => {
        setIsBannerOpen(true);
    }, []);

    useEffect(() => {
        const onMessage = (event: MessageEvent) => {
            if (event?.data?.type === 'OPEN_TRADE') {
                const txHash = event.data.txHash as string | undefined;
                if (!txHash || !address) {
                    return;
                }
                // Start rolling right away; the user's pick only reveals it.
                const claims = fetchHold353Claims(address, txHash);
                claimsRef.current = claims;
                setIsBannerOpen(false);
                setIsDrawOpen(true);
                claims.catch((e) => {
                    if (claimsRef.current === claims) {
                        toastApp.error(getErrorMessage(e));
                        setIsDrawOpen(false);
                    }
                });
            } else if (event?.data?.type === 'CLOSE_TRADE') {
                setIsBannerOpen(true);
            }
        };
        window.addEventListener('message', onMessage);
        return () => window.removeEventListener('message', onMessage);
    }, [address]);

    const handleDraw = useCallback(async () => {
        try {
            const resp = await claimsRef.current;
            if (!resp) {
                return null;
            }
            const won = resp.claims.some((c) => c.reward === 1);
            // A losing roll is only recorded on-chain; skip the extra tx.
            if (won) {
                await sendHold353Claims(resp);
            }
            return won ? 'win' : 'lose';
        } catch (e) {
            // fetch failures are already toasted (and the sheet closed) above
            if (claimsRef.current) {
                const failed = await claimsRef.current.then(
                    () => false,
                    () => true
                );
                if (!failed) {
                    toastApp.error(getErrorMessage(e));
                }
            }
            return null;
        }
    }, []);

    return (
        <>
            <LuckyDrawBannerPopup
                isOpen={isBannerOpen}
                onClose={() => setIsBannerOpen(false)}
                onOpenNow={() => {
                    setIsBannerOpen(false);
                    goTradeMax();
                }}
            />
            <LuckyDrawPopup
                isOpen={isDrawOpen}
                onDraw={handleDraw}
                onClose={() => setIsDrawOpen(false)}
            />
        </>
    );
}
